/**
 * Entrenar — MOBILE. Es la pantalla que define la app.
 *
 * Dos caminos, a propósito:
 * - **Un toque**: el botón ＋ de la tarjeta registra la serie que dice adentro ("＋ 40 lb × 12"),
 *   igual a la anterior. Es el caso normal, y no requiere abrir nada ni escribir nada. Cuando no
 *   hay nada que repetir —toda la primera sesión— el ＋ abre la hoja con el peso vacío en vez de
 *   no hacer nada.
 * - **Dos toques**: "Ajustar" abre la hoja inferior para cambiar peso o reps con el stepper, que
 *   se mueve de a incrementos reales de esa máquina, o para omitir con motivo. Es un botón
 *   visible y permanente: antes el único camino era tocar el nombre, que no se ve que sea un
 *   botón, explicado por una pista que se descarta para siempre.
 *
 * La hoja sube desde abajo porque el pulgar ya está abajo. Un diálogo centrado obligaría a
 * recolocar la mano en medio de una serie.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  fechaLarga,
  formatearDuracion,
  formatearNumero,
  type Ejercicio,
  type Hallazgo,
  type Maquina,
  type MotivoOmision,
} from '@mg/core';

import {
  MOTIVOS,
  motivoHumano,
  useRegistro,
  useTemporizador,
  type EjercicioEnSesion,
} from '../features/useRegistro.ts';
import { HojaInferior } from '../ui/Shell.mobile.tsx';
import { Pista } from '../ui/Pista.tsx';
import { Aviso, Campo, Icono, Insignia, Peso, Pila, Stepper, Vacio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';

export function RegistroMobile(): ReactNode {
  const r = useRegistro();
  const t = useTemporizador();
  const { ir } = useRuta();
  const [abierto, setAbierto] = useState<string | null>(null);
  const [agregando, setAgregando] = useState(false);
  const [erroresHoja, setErroresHoja] = useState<Hallazgo[]>([]);
  const [erroresDelMas, setErroresDelMas] = useState<Record<string, Hallazgo[]>>({});
  const banda = useRef<HTMLElement>(null);

  // Al cerrar, el resumen tiene que quedar a la vista: cerrar desde el pie de una lista larga
  // dejaba al usuario mirando el mismo lugar de siempre, sin ninguna señal de que algo pasó.
  useEffect(() => {
    if (r.cerrada) banda.current?.scrollIntoView({ block: 'start' });
  }, [r.cerrada]);

  if (r.datos.cargando) return <Vacio>Cargando…</Vacio>;

  if (!r.sesion) {
    const conteo = r.conteoDiaSugerido;

    return (
      <>
        <div className="tarjeta">
          <span className="eyebrow">Hoy</span>
          <h2 className="titulo" style={{ marginTop: 4 }}>
            {r.diaSugerido?.nombre ?? 'Sin rutina configurada'}
          </h2>
          {/* Sin ninguna sesión cerrada no hay "última sesión" de la cual venir: hablarle de una
              rotación a alguien que nunca entrenó es contarle un pasado que no tuvo. */}
          <p className="nota" style={{ marginTop: 'var(--e2)' }}>
            {r.diaSugerido
              ? `${r.esPrimerDia ? 'Es el primer día de tu rutina.' : 'Toca por rotación después de tu última sesión.'} ${conteo.resueltos} ${conteo.resueltos === 1 ? 'ejercicio' : 'ejercicios'}.`
              : 'Sin rutina no hay nada que registrar: primero cargá los días y sus ejercicios.'}
          </p>
        </div>

        {/* Avisar ANTES de abrir: una sesión vacía se puede abrir igual —para hacer sólo cardio es
            legítimo— pero llegar a "0 / 0 series" sin haberlo elegido es lo que confundía. */}
        {r.diaSugerido && conteo.resueltos === 0 && (
          <Aviso tono="atencion" titulo="Este día no tiene ejercicios">
            {conteo.enRutina > 0
              ? `Tu rutina tiene ${conteo.enRutina} para este día, pero no están en el catálogo de este gimnasio.`
              : 'No hay ninguno cargado para este día.'}{' '}
            Podés abrir la sesión igual y elegirlos ahí, o arreglar la rutina.
          </Aviso>
        )}

        {r.error && <Aviso tono="importante">{r.error}</Aviso>}

        <button
          type="button"
          className="boton boton--principal boton--registrar"
          onClick={() => void r.abrir()}
          disabled={r.abriendo || !r.diaSugerido}
        >
          {r.abriendo ? 'Abriendo…' : 'Empezar la sesión'}
        </button>

        {!r.diaSugerido && (
          <p className="nota--fina" style={{ textAlign: 'center' }}>
            Falta tu rutina: sin un día cargado no hay ejercicios que abrir.
          </p>
        )}

        <button type="button" className="boton boton--fantasma" onClick={() => ir('ajustes', 'rutina')}>
          {r.diaSugerido ? 'Editar la rutina' : 'Cargar mi rutina'}
        </button>

        {r.datos.rutinaDias.length > 1 && (
          <div className="columna" style={{ gap: 'var(--e2)' }}>
            <span className="eyebrow">O elegí otro día</span>
            {r.datos.rutinaDias
              .filter((d) => d.id !== r.diaSugerido?.id)
              .map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="boton boton--fantasma"
                  onClick={() => void r.abrir(d.id)}
                >
                  {d.nombre}
                </button>
              ))}
          </div>
        )}
      </>
    );
  }

  const item = r.ejercicios.find((e) => e.ejercicio.id === abierto) ?? null;
  const cerrada = r.sesion.estado === 'completada';

  return (
    <>
      {/* Con la sesión cerrada la cabecera queda mínima: el cierre se cuenta UNA vez, en la banda
          de abajo, que es la que además tiene los botones. Antes el mismo dato —series, omitidos—
          se leía dos veces con ochenta píxeles de diferencia. */}
      <div className="fila fila--entre" style={{ alignItems: 'flex-start' }}>
        <div>
          <span className="eyebrow">
            {cerrada ? fechaLarga(r.sesion.fecha) : `En curso · ${fechaLarga(r.sesion.fecha)}`}
          </span>
          {!cerrada && (
            <>
              <p className="lectura" style={{ fontSize: 15 }}>
                {r.totalCompletadas} / {r.totalPlanificadas} series
              </p>
              {r.omitidos.length > 0 && (
                <p className="nota--fina">
                  {r.omitidos.length} {r.omitidos.length === 1 ? 'ejercicio omitido' : 'ejercicios omitidos'} (
                  {r.omitidos.map((o) => o.motivo).join(' · ')})
                </p>
              )}
            </>
          )}
        </div>

        {!cerrada &&
          (t.corriendo ? (
            <button type="button" className="boton boton--chico" onClick={t.parar}>
              <span className="lectura" style={{ fontSize: 17 }}>
                {formatearDuracion(t.segundos)}
              </span>
            </button>
          ) : (
            <button type="button" className="boton boton--chico boton--fantasma" onClick={t.arrancar}>
              <Icono nombre="historial" tamano={15} />
              Descanso
            </button>
          ))}
      </div>

      {/* El cierre de verdad: qué hiciste, qué quedó afuera y por qué, qué sigue y cómo salir.
          Antes acá había un "SESIÓN CERRADA · 12/15" y un botón de descanso. */}
      {cerrada && r.resumen && (
        <section ref={banda} className="tarjeta" style={{ borderColor: 'var(--agua)' }}>
          <span className="eyebrow">Sesión cerrada</span>
          <p className="nota" style={{ marginTop: 4 }}>
            <span className="lectura">{r.resumen.seriesCompletadas}</span> series ·{' '}
            <span className="lectura">{formatearNumero(r.resumen.volumenKg, 0)} kg</span> levantados
          </p>

          {/* Lo omitido, uno por línea y con su destino: lo que se arrastra vuelve solo en la
              próxima sesión, y eso hay que decirlo acá o el usuario lo da por perdido. */}
          {r.resumen.omitidos.length > 0 && (
            <div className="columna" style={{ gap: 2, marginTop: 'var(--e2)' }}>
              <span className="eyebrow">
                {r.resumen.omitidos.length === 1 ? '1 ejercicio omitido' : `${r.resumen.omitidos.length} ejercicios omitidos`}
              </span>
              {r.resumen.omitidos.map((o, i) => (
                <p key={`${o.nombre}-${i}`} className="nota--fina">
                  {o.nombre} · {o.motivo}
                  {o.seArrastra && ' · pasa a la próxima sesión'}
                </p>
              ))}
            </div>
          )}

          {r.resumen.proxima && (
            <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
              La próxima: {r.resumen.proxima.fecha ? `${fechaLarga(r.resumen.proxima.fecha)} · ` : ''}
              {r.resumen.proxima.nombre}
            </p>
          )}

          {/* Apilados y a lo ancho: en fila, con 390 px de pantalla, las etiquetas se partían
              ("Reabrir la / sesión"). */}
          <div className="columna" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
            <button type="button" className="boton boton--principal" onClick={() => ir('inicio')}>
              Volver al inicio
            </button>
            <button type="button" className="boton" onClick={() => void r.reabrir()}>
              Reabrir la sesión
            </button>
            {/* El historial vive detrás de "Más": nombrarlo sin traer el botón era mandar a
                alguien a un lugar que no sabe encontrar. */}
            <button type="button" className="boton boton--fantasma" onClick={() => ir('historial')}>
              <Icono nombre="historial" tamano={18} />
              Abrir el historial
            </button>
          </div>
          <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
            Reabrila si te acordaste de una serie. Para corregir un peso ya registrado, andá al
            historial.
          </p>
        </section>
      )}

      {/* La sesión en curso no se cierra sola al terminar el día, así que puede ser de anteayer.
          Decirlo alcanza para no anotar el martes lo que se hizo el jueves. */}
      {!cerrada && !r.esDeHoy && (
        <Aviso tono="atencion" titulo="Esta sesión no es de hoy">
          Quedó abierta del {fechaLarga(r.sesion.fecha)}. Podés seguir anotando ahí, o cerrarla y
          empezar la de hoy.
          <button
            type="button"
            className="boton"
            style={{ marginTop: 'var(--e3)', width: '100%' }}
            onClick={() => void r.cerrarYEmpezarHoy()}
            disabled={r.abriendo}
          >
            {r.abriendo ? 'Abriendo…' : `Cerrar la del ${fechaLarga(r.sesion.fecha)} y empezar la de hoy`}
          </button>
        </Aviso>
      )}

      {r.reorden?.cambia && !cerrada && (
        <Aviso tono="atencion" titulo="Músculo repetido de ayer">
          {r.reorden.motivo}
        </Aviso>
      )}

      {r.ejercicios.length > 0 && !cerrada && (
        <Pista clave="boton-mas">
          El botón ＋ registra la serie que dice adentro, sin abrir nada. "Ajustar" abre el
          ejercicio para cambiar el peso, las reps, o decir que no lo pudiste hacer.
        </Pista>
      )}

      {/* La sesión vacía tiene salida. Antes acá no había nada: la lista se renderizaba en blanco
          y los dos únicos botones de la pantalla eran el cardio y cerrar, así que el cardio
          parecía lo que la app te estaba pidiendo. */}
      {r.sesionVacia && (
        <section className="tarjeta" style={{ borderColor: 'var(--ambar)' }}>
          <span className="eyebrow">Sesión sin ejercicios</span>
          <p className="nota" style={{ marginTop: 'var(--e2)' }}>
            {r.sesionVacia}
          </p>
          {!cerrada && (
            <button
              type="button"
              className="boton boton--principal"
              style={{ marginTop: 'var(--e3)', width: '100%' }}
              onClick={() => setAgregando(true)}
            >
              <Icono nombre="mas" tamano={18} />
              Elegir un ejercicio
            </button>
          )}
          <button
            type="button"
            className="boton boton--fantasma boton--chico"
            style={{ marginTop: 'var(--e2)', width: '100%' }}
            onClick={() => ir('ajustes', 'rutina')}
          >
            Arreglar la rutina
          </button>
        </section>
      )}

      <div className="columna" style={{ gap: 'var(--e3)' }}>
        {r.ejercicios.map((e) => (
          <TarjetaEjercicio
            key={e.ejercicio.id}
            item={e}
            cerrada={cerrada}
            errores={erroresDelMas[e.ejercicio.id] ?? []}
            onAbrir={() => {
              setErroresHoja([]);
              setAbierto(e.ejercicio.id);
            }}
            onQuitar={() => void r.quitar(e)}
            onRepetir={async () => {
              const res = await r.repetir(e);

              // El ＋ nunca se queda mudo: sin serie anterior no hay nada que copiar, así que
              // abre la hoja para que el usuario ponga el peso que usó.
              if (res.tipo === 'sin_referencia') {
                setErroresHoja([]);
                setAbierto(e.ejercicio.id);
                return;
              }

              setErroresDelMas((prev) => ({
                ...prev,
                [e.ejercicio.id]: res.resultado.ok
                  ? []
                  : res.resultado.hallazgos.filter((h) => h.severidad === 'error'),
              }));
              if (res.resultado.ok) t.arrancar();
            }}
          />
        ))}
      </div>

      {!cerrada && (
        <>
          {/* Agregar y cardio, al mismo nivel: la sesión de hoy no tiene por qué ser la que estaba
              planificada, y el cardio no es más importante que meter un ejercicio de más. */}
          {r.ejercicios.length > 0 && (
            <button type="button" className="boton boton--fantasma" onClick={() => setAgregando(true)}>
              <Icono nombre="mas" tamano={18} />
              Agregar un ejercicio
            </button>
          )}

          <button type="button" className="boton boton--fantasma" onClick={() => ir('ingesta')}>
            <Icono nombre="camara" tamano={18} />
            Cargar el cardio con una foto
          </button>

          <div className="columna" style={{ gap: 'var(--e2)' }}>
            <button type="button" className="boton" onClick={() => void r.cerrar()}>
              Cerrar la sesión
            </button>
            {r.seriesPendientes > 0 && (
              <p className="nota--fina" style={{ textAlign: 'center' }}>
                Se descartan {r.seriesPendientes}{' '}
                {r.seriesPendientes === 1 ? 'serie no hecha' : 'series no hechas'}. Si alguna no la
                hiciste por algo, decilo antes en "Ajustar → No pude hacerlo": lo omitido con motivo
                se arrastra a la próxima sesión.
              </p>
            )}
          </div>
        </>
      )}

      {item && (
        <HojaInferior titulo={item.ejercicio.nombre} onCerrar={() => setAbierto(null)}>
          <HojaSerie
            item={item}
            errores={erroresHoja}
            onProgreso={() => {
              setAbierto(null);
              ir('progreso', item.ejercicio.id);
            }}
            onRegistrar={async (valores) => {
              const res = await r.registrar(item, valores);
              if (!res.ok) {
                setErroresHoja(res.hallazgos.filter((h) => h.severidad === 'error'));
                return;
              }
              setErroresHoja([]);
              setAbierto(null);
              t.arrancar();
            }}
            onOmitir={async (motivo) => {
              await r.omitir(item, motivo);
              setAbierto(null);
            }}
          />
        </HojaInferior>
      )}

      {agregando && (
        <HojaInferior titulo="Agregar un ejercicio" onCerrar={() => setAgregando(false)}>
          <HojaAgregar
            disponibles={r.agregables}
            maquinaDe={r.datos.maquinaDe}
            onElegir={async (id) => {
              await r.agregar(id);
              setAgregando(false);
            }}
          />
        </HojaInferior>
      )}
    </>
  );
}

/**
 * Elegir un ejercicio del catálogo. Con filtro de texto porque el catálogo tiene decenas y
 * scrollear una lista de treinta con una mano entre series no es una opción.
 */
function HojaAgregar({
  disponibles,
  maquinaDe,
  onElegir,
}: {
  disponibles: Ejercicio[];
  maquinaDe: (ejercicioId: string) => Maquina | null;
  onElegir: (ejercicioId: string) => Promise<void>;
}): ReactNode {
  const [filtro, setFiltro] = useState('');
  const norma = (s: string): string =>
    s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

  const visibles = filtro.trim()
    ? disponibles.filter((e) => norma(e.nombre).includes(norma(filtro.trim())))
    : disponibles;

  return (
    <>
      <Campo
        etiqueta="Buscar"
        texto
        placeholder="Ej. press, curl, remo…"
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
      />

      {visibles.length === 0 ? (
        <Vacio>
          {disponibles.length === 0
            ? 'Todos los ejercicios del catálogo ya están en esta sesión.'
            : `Ningún ejercicio coincide con "${filtro.trim()}".`}
        </Vacio>
      ) : (
        <div className="columna" style={{ gap: 'var(--e2)' }}>
          {visibles.map((e) => {
            const maquina = maquinaDe(e.id);
            return (
              <button
                key={e.id}
                type="button"
                className="boton"
                onClick={() => void onElegir(e.id)}
                style={{ justifyContent: 'flex-start', paddingLeft: 'var(--e4)' }}
              >
                <span className="columna" style={{ alignItems: 'flex-start', gap: 0 }}>
                  {e.nombre}
                  <span className="nota--fina" style={{ fontWeight: 400 }}>
                    {maquina ? maquina.etiqueta_ubicacion : 'ubicación sin registrar'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <p className="nota--fina">
        Entra con 3 series pendientes, como cualquier otro. Si lo agregaste por error, se saca con
        "Quitar".
      </p>
    </>
  );
}

function TarjetaEjercicio({
  item,
  cerrada,
  errores,
  onAbrir,
  onQuitar,
  onRepetir,
}: {
  item: EjercicioEnSesion;
  cerrada: boolean;
  errores: Hallazgo[];
  onAbrir: () => void;
  onQuitar: () => void;
  onRepetir: () => Promise<void>;
}): ReactNode {
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);
  const hechas = item.completadas.length;
  const objetivo = Math.max(item.seriesObjetivo, hechas);
  const marca = item.sugerencia.ultimaVez;
  const activo = !cerrada && !item.omitida;
  const consejo =
    (item.sugerencia.tipo === 'subir' || item.sugerencia.tipo === 'cambiar_variable') &&
    item.sugerencia.pesoSugerido !== null;

  const encabezado = (
    <>
      <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>{item.ejercicio.nombre}</span>
        {item.arrastradoDe && <Insignia tono="ambar">viene de antes</Insignia>}
        {item.estancado && <Insignia tono="ambar">estancado</Insignia>}
        {item.omitida && <Insignia tono="alerta">omitido · {motivoHumano(item.omitida.motivo_omision)}</Insignia>}
      </div>

      <p className="nota--fina" style={{ marginTop: 2 }}>
        {/* Un dato faltante sin salida no sirve de nada: si nadie anotó dónde está la máquina,
            la línea dice también dónde se arregla. Se anota en la hoja, en "Ajustar". */}
        {item.maquina
          ? item.maquina.etiqueta_ubicacion
          : activo
            ? 'Ubicación sin registrar — anotala en "Ajustar"'
            : 'Ubicación sin registrar'}
        {marca && (
          <>
            {' · última vez '}
            <span className="lectura">
              {formatearNumero(marca.peso)} {marca.unidad} × {marca.reps.join('/')}
            </span>
          </>
        )}
      </p>
    </>
  );

  return (
    <div className="tarjeta" style={{ opacity: item.omitida ? 0.55 : 1 }}>
      {/* Con la sesión cerrada el nombre deja de ser un botón: abría la hoja y dejaba registrar
          series en una sesión que ya estaba cerrada. */}
      {activo ? (
        <button
          type="button"
          onClick={onAbrir}
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            textAlign: 'left',
            color: 'inherit',
            width: '100%',
            minHeight: 44,
          }}
        >
          {encabezado}
        </button>
      ) : (
        <div>{encabezado}</div>
      )}

      <div style={{ marginTop: 'var(--e3)' }}>
        <Pila
          estados={Array.from({ length: objetivo }, (_, i) => (i < hechas ? 'llena' : 'hueca'))}
          alto={8}
          etiqueta={`${hechas} de ${objetivo} series`}
        />
      </div>

      {/* Numeradas y con separador: cuatro series seguidas ("30 kg × 10  30 kg × 10  …") se leían
          como una sola tira y no se sabía dónde terminaba una. El número es texto de la app, así
          que no va en monoespaciada; la lectura sí. */}
      {item.completadas.length > 0 && (
        <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)', flexWrap: 'wrap' }}>
          {item.completadas.map((s, i) => (
            <span key={s.id} className="nota--fina">
              {i > 0 && <span className="apagado" style={{ marginRight: 'var(--e2)' }}>·</span>}
              <span className="apagado">{i + 1}.</span>{' '}
              {/* Sin la clase .unidad: a 12 px de base quedaría en 5 px, ilegible. */}
              <span className="lectura">
                {formatearNumero(s.peso)} {s.unidad} × {s.reps}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* La sugerencia es un consejo aparte, nunca el valor del ＋: el ＋ repite, y lo dice
          adentro. Cuando los dos números eran distintos y ninguno estaba escrito, la tarjeta
          proponía una tercera cosa que nadie había pedido. */}
      {hechas === 0 && activo && consejo && (
        <p className="nota--fina" style={{ marginTop: 'var(--e2)', color: 'var(--ambar)' }}>
          {item.sugerencia.tipo === 'subir' ? 'Sugerencia: subí un escalón, ' : 'Sugerencia: cambiá la variable, '}
          <span className="lectura">
            {formatearNumero(item.sugerencia.pesoSugerido!)} {item.sugerencia.unidad} ×{' '}
            {item.sugerencia.repsSugeridas ?? '?'}
          </span>{' '}
          — se ajusta en "Ajustar".
        </p>
      )}

      {hechas === 0 && activo && item.proxima.origen === 'sin_referencia' && (
        <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
          Primera vez con este ejercicio: tocá ＋ y poné el peso que uses. La próxima ya viene solo.
        </p>
      )}

      {errores.length > 0 && (
        <div style={{ marginTop: 'var(--e2)' }}>
          <Aviso tono="importante" titulo="No se registró">
            {errores.map((h) => h.mensaje).join(' ')}
          </Aviso>
        </div>
      )}

      {activo && (
        <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
          <button
            type="button"
            className="boton"
            onClick={onAbrir}
            style={{ flex: 1, minHeight: 48, padding: '0 var(--e3)' }}
          >
            Ajustar
          </button>

          <button
            type="button"
            className="boton boton--principal"
            onClick={() => void onRepetir()}
            aria-label={`Registrar una serie de ${item.ejercicio.nombre} igual a la anterior`}
            style={{ flex: 1.6, minHeight: 60, padding: '0 var(--e3)' }}
          >
            <Icono nombre="mas" tamano={24} />
            {item.proxima.peso !== null ? (
              <span className="lectura" style={{ fontSize: 16 }}>
                {formatearNumero(item.proxima.peso)} {item.proxima.unidad} × {item.proxima.reps}
              </span>
            ) : (
              <span style={{ fontSize: 15 }}>Primera serie</span>
            )}
          </button>
        </div>
      )}

      {/* Sacar de la sesión lo que se agregó por error. Sólo mientras no tenga nada hecho: una
          serie registrada no se borra desde acá. */}
      {activo && hechas === 0 && (
        <div style={{ marginTop: 'var(--e2)' }}>
          {confirmandoQuitar ? (
            <div className="fila" style={{ gap: 'var(--e2)' }}>
              <span className="nota--fina" style={{ flex: 1 }}>
                ¿Lo sacamos de esta sesión?
              </span>
              <button
                type="button"
                className="boton boton--chico"
                onClick={() => setConfirmandoQuitar(false)}
              >
                No
              </button>
              <button type="button" className="boton boton--chico boton--peligro" onClick={onQuitar}>
                Sí, quitar
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="boton boton--fantasma boton--chico"
              onClick={() => setConfirmandoQuitar(true)}
            >
              Quitar de la sesión
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function HojaSerie({
  item,
  errores,
  onRegistrar,
  onOmitir,
  onProgreso,
}: {
  item: EjercicioEnSesion;
  errores: Hallazgo[];
  onRegistrar: (v: { peso: number; reps: number; unidad: typeof item.unidad; rir?: number | null }) => Promise<void>;
  onOmitir: (motivo: MotivoOmision) => Promise<void>;
  onProgreso: () => void;
}): ReactNode {
  const marca = item.sugerencia.ultimaVez;
  // El peso vive como texto para poder estar VACÍO. Precargarlo en 0 hacía que "Registrar 0 kg ×
  // 10" se guardara sin que nada se quejara: 0 es un peso válido para el validador.
  const [pesoTexto, setPesoTexto] = useState<string>(
    item.proxima.peso === null ? '' : formatearNumero(item.proxima.peso, 2),
  );
  const [reps, setReps] = useState<number>(item.proxima.reps);
  const [rir, setRir] = useState<number | null>(null);
  const [mostrandoMotivos, setMostrandoMotivos] = useState(false);

  const peso = Number(pesoTexto.replace(',', '.'));
  const pesoListo = pesoTexto.trim() !== '' && Number.isFinite(peso) && peso > 0;
  const identicaALaAnterior = marca !== null && pesoListo && peso === marca.peso && marca.reps.includes(reps);

  return (
    <>
      {marca ? (
        <div className="fila fila--entre">
          <div>
            <span className="eyebrow">Última vez · {marca.fecha}</span>
            <p style={{ marginTop: 2 }}>
              <Peso valor={marca.peso} unidad={marca.unidad} /> <span className="apagado">×</span>{' '}
              <span className="lectura">{marca.reps.join(' / ')}</span>
            </p>
          </div>
          {item.maquina && (
            <span className="nota--fina" style={{ textAlign: 'right' }}>
              {item.maquina.etiqueta_ubicacion}
              <br />
              de {formatearNumero(item.incremento)} en {formatearNumero(item.incremento)} {item.maquina.unidad_placa}
            </span>
          )}
        </div>
      ) : (
        <Aviso tono="info" titulo="Primera vez con este ejercicio">
          {item.sugerencia.motivo}
        </Aviso>
      )}

      {item.sugerencia.tipo !== 'mantener' && marca && (
        <Aviso tono={item.sugerencia.tipo === 'cambiar_variable' ? 'atencion' : 'info'}>
          {item.sugerencia.motivo}
        </Aviso>
      )}

      {/* Sin referencia no hay de dónde subir ni bajar: primero hay que escribir un número, y el
          campo arranca vacío y con el foco puesto. Con referencia manda el stepper, que se mueve
          de a escalones reales de la máquina. */}
      {item.proxima.peso === null ? (
        <Campo
          etiqueta="Peso"
          inputMode="decimal"
          autoFocus
          value={pesoTexto}
          onChange={(e) => setPesoTexto(e.target.value)}
          placeholder={`en ${item.unidad}`}
          ayuda={
            item.maquina
              ? `Esta máquina sube de ${formatearNumero(item.incremento)} en ${formatearNumero(item.incremento)} ${item.maquina.unidad_placa}.`
              : `En ${item.unidad}, como lo leíste en la máquina.`
          }
        />
      ) : (
        <Stepper
          etiqueta="Peso"
          valor={peso}
          incremento={item.incremento}
          onCambio={(v) => setPesoTexto(formatearNumero(v, 2))}
          unidad={item.unidad}
        />
      )}

      <Stepper etiqueta="Reps" valor={reps} incremento={1} min={1} onCambio={setReps} />

      <div>
        <span className="eyebrow etiqueta">Reps en reserva (opcional)</span>
        <div className="fila" style={{ gap: 'var(--e2)' }}>
          {[0, 1, 2, 3].map((n) => (
            <button
              key={n}
              type="button"
              className={rir === n ? 'boton boton--chico boton--principal' : 'boton boton--chico'}
              onClick={() => setRir(rir === n ? null : n)}
              style={{ flex: 1 }}
            >
              {n === 0 ? 'al fallo' : `+${n}`}
            </button>
          ))}
        </div>
      </div>

      {errores.length > 0 && (
        <Aviso tono="importante" titulo="No se guardó">
          {errores.map((h) => h.mensaje).join(' ')}
        </Aviso>
      )}

      <button
        type="button"
        className="boton boton--principal boton--registrar"
        disabled={!pesoListo}
        onClick={() => void onRegistrar({ peso, reps, unidad: item.unidad, rir })}
      >
        <span>
          {pesoListo ? `Registrar ${formatearNumero(peso)} ${item.unidad} × ${reps}` : 'Registrar la serie'}
        </span>
        {identicaALaAnterior && (
          <span className="nota--fina" style={{ color: '#16171b99' }}>
            igual que la anterior
          </span>
        )}
      </button>

      {!pesoListo && (
        <p className="nota--fina" style={{ textAlign: 'center' }}>
          Poné el peso que usaste — la próxima vez viene solo.
        </p>
      )}

      <button type="button" className="boton boton--fantasma boton--chico" onClick={onProgreso}>
        Ver su progresión
      </button>

      {!mostrandoMotivos ? (
        <button type="button" className="boton boton--fantasma boton--chico" onClick={() => setMostrandoMotivos(true)}>
          No pude hacerlo
        </button>
      ) : (
        <div className="columna" style={{ gap: 'var(--e2)' }}>
          <span className="eyebrow">¿Por qué?</span>
          {MOTIVOS.map((m) => (
            <button key={m.valor} type="button" className="boton" onClick={() => void onOmitir(m.valor)}>
              {m.etiqueta}
            </button>
          ))}
          <p className="nota--fina">
            Lo que quede sin hacer por equipo ocupado o falta de tiempo se ofrece en la próxima
            sesión. Si te dolió, no se arrastra solo.
          </p>
        </div>
      )}
    </>
  );
}
